import { useAppSelector, useAppDispatch } from "@/services/hooks";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import { useUserRole } from "@/services/userServices/useUserRole";
import React, { useEffect, useMemo, useState, Fragment } from "react";
import { Button, UncontrolledTooltip } from "reactstrap";
import { Link } from "react-router-dom";

import { ToastContainer } from "react-toastify";
import ConfirmDelete from "@/components/ConfirmDelete";

import {
  getPosts,
  getBlog,
  deletePost,
  clearMessage,
} from "@/services/blogServices/blogActions";
import { getCurrentProfile } from "@/services/userServices/profileAction";

import NotificationAlertOptions from "@/layouts/Alerts";
import ReferrersDialog from "@/components/ReferrersDialog";
import { isDeleteRefusal, refusalMessage } from "@/services/deleteRefusal";
import { ADMIN } from "@/routes/Roles";

import SitePostsView from "./SitePostsView";
import type { BlogAuthorCard } from "./SitePostsView";

export default function SitePostsContainer() {
  const dispatch = useAppDispatch();


  const blog = useAppSelector((s) => s.blog);
  const auth = useAppSelector((s) => s.auth);
  const site = useAppSelector((s) => s.sites);
  const permission = useCurrentPermission();
  const profile = useAppSelector((s) => s.profile?.profile);
  const userRole = useUserRole();
  const [viewAuthors, setViewAuthors] = useState(false);
  const [author, setAuthor] = useState<BlogAuthorCard>({});
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);


  useEffect(() => {
    dispatch(getBlog());
    dispatch(getPosts());
    dispatch(getCurrentProfile());
  }, [dispatch]);


  useEffect(() => {
    if (blog?.message) {
      NotificationAlertOptions("success", blog.message);
      dispatch(clearMessage());
    }
  }, [blog?.message, dispatch]);

  
  const visiblePosts = useMemo(() => {
    const allPosts = blog?.posts || [];
    if (!userRole) return [];
    if (userRole === ADMIN) return allPosts;
    if (profile?.name) {
      return allPosts.filter(
        (p) => p?.author?.name?.toLowerCase() === profile.name.toLowerCase()
      );
    }
    return [];
  }, [blog?.posts, userRole, profile?.name]);


  // SCRUM-1148: the search moved to TableWrapper's global filter, but the ROLE
  // FILTER above and the sort below stay here and feed `data` — the parent
  // ticket is explicit that both are features rather than list chrome. Handing
  // TableWrapper pre-sorted rows keeps newest-first as the default order while
  // the headers additionally become sortable.
  const sortedPosts = useMemo(() => {
    return [...visiblePosts].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0);
      const dateB = new Date(b.createdAt || 0);
      return dateB.getTime() - dateA.getTime(); // Newest first
    });
  }, [visiblePosts]);
  const confirmDelete = (idPost) => {
    setAlertNode(
      <ConfirmDelete
        title="Would you like to remove this Post?"
        onClose={hideAlert}
        onConfirm={() => dispatch(deletePost(idPost)).unwrap()}
        onSuccess={() =>
          NotificationAlertOptions("success", "Post Deleted Successfully")
        }
        onError={showReferrers}
      >
        Delete Operation
      </ConfirmDelete>
    );
  };

  const hideAlert = () => setAlertNode(null);

  // SCRUM-2355: the SCRUM-2352 pop-up for a delete the server refused.
  const showReferrers = (err: unknown) => {
    if (!isDeleteRefusal(err)) return;
    setAlertNode(
      <ReferrersDialog
        message={refusalMessage(err)}
        referrers={err.referrers}
        onClose={hideAlert}
      />
    );
  };

  const openAuthor = (a) => {
    setAuthor(a || {});
    setViewAuthors(true);
  };

  const closeAuthor = () => setViewAuthors(false);

  /**
   * SCRUM-1148: the container only describes the columns now.
   *
   * The Author column carries an `accessorFn` even though its cell renders a
   * button. TableWrapper's global filter only searches columns that expose a
   * value, and the search this replaces matched title OR author name — without
   * the accessor, searching for an author would silently stop finding anything,
   * a migration that looks complete while quietly halving what the box does.
   */
  const columns = useMemo(() => {
    const canEdit = !!(permission && (permission.publish || permission.edit));

    const AuthorCell = ({ row }) => {
      const p = row.original;
      return (
        <Button
          className="px-0"
          color="link"
          type="button"
          onClick={() => openAuthor(p?.author)}
          aria-label={`Open author details for ${p?.author?.name || "unknown"}`}
        >
          {p?.author?.name || "User Not Found"}
        </Button>
      );
    };
    AuthorCell.displayName = "PostAuthorCell";

    const PictureCell = ({ row }) => {
      const p = row.original;
      if (!p?.picture) return "N/A";
      return (
        <img
          alt={p?.alt_pic || p?.title || "Post image"}
          className="avatar rounded-circle mr-3"
          src={p.picture}
          loading="lazy"
        />
      );
    };
    PictureCell.displayName = "PostPictureCell";

    const TypeCell = ({ row }) => (
      <span className="text-muted">
        {row.original?.type ? (
          <b className="font-italic text-info">NEWS</b>
        ) : (
          "POST"
        )}
      </span>
    );
    TypeCell.displayName = "PostTypeCell";

    const base = [
      {
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => <span className="text-muted">{row.original?.title}</span>,
      },
      {
        id: "author",
        header: "Author",
        accessorFn: (row) => row?.author?.name || "",
        cell: AuthorCell,
      },
      { id: "picture", header: "Picture", cell: PictureCell },
      { id: "type", header: "Type", cell: TypeCell },
    ];

    if (!canEdit) return base;

    const ActionsCell = ({ row }) => {
      const p = row.original;
      const key = p?._id || p?.slug || "row";
      const updId = `tooltip-upd-${key}`;
      const delId = `tooltip-del-${key}`;
      return (
        <div className="table-actions">
          <UncontrolledTooltip delay={0} trigger="hover focus" target={updId}>
            Edit
          </UncontrolledTooltip>
          <UncontrolledTooltip delay={0} trigger="hover focus" target={delId}>
            Delete
          </UncontrolledTooltip>

          <Button
            className="mr-4"
            color="primary"
            size="sm"
            id={updId}
            type="button"
            tag={Link}
            to={`/admin/update-post/${p?._id}`}
            aria-label={`Edit post ${p?.title || ""}`}
          >
            <i className="fas fa-pen-to-square" aria-hidden="true" />
            <span className="ml-1">Edit</span>
          </Button>

          <Button
            className="mr-4"
            color="danger"
            size="sm"
            id={delId}
            type="button"
            onClick={() => confirmDelete(p?._id)}
            aria-label={`Delete post ${p?.title || ""}`}
          >
            <i className="fas fa-trash" aria-hidden="true" />
            <span className="ml-1">Delete</span>
          </Button>
        </div>
      );
    };
    ActionsCell.displayName = "PostActionsCell";

    return [...base, { id: "actions", header: "Actions", cell: ActionsCell }];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- cell handlers are new closures each render; columns rebuild when permission changes
  }, [permission]);

  return (
    <Fragment>
      {/* Notifications */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      {/* Alert */}
      {alertNode}
      <SitePostsView
        posts={sortedPosts}
        columns={columns}
        viewAuthors={viewAuthors}
        author={author}
        onCloseAuthor={closeAuthor}
      />
    </Fragment>
  );
}
